import { useCallback, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';

interface DecodedTokenType {
  IdUid: string;
  Name: string;
  Role: string;
  UserType: number;
  exp: number;
  iss: string;
  aud: string;
}

export function useLocalStorage<T>(
  key: string,
  defaultValue?: T
): { value: string; setValue: (data: string) => void; removeToken: () => void } {
  return useStorage<T>(key, window.localStorage, defaultValue);
}

export function useSessionStorage<T>(key: string, defaultValue?: T) {
  return useStorage<T>(key, window.sessionStorage, defaultValue);
}
export function useDecodeToken() {
  const localToken = window?.localStorage?.getItem('tms-token');
  if (localToken) {
    try {
      // useLocalStorage stores values as JSON.stringify(value), so the raw
      // localStorage string may be `'"jwt..."'` — parse it first if needed.
      const tokenStr = localToken.startsWith('"') ? JSON.parse(localToken) : localToken;
      const token: DecodedTokenType = jwtDecode(tokenStr);
      return token;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

function useStorage<T>(key: string, storageObject: Storage, defaultValue?: T) {
  const [value, setValue] = useState(() => {
    const jsonValue = storageObject.getItem(key);
    if (jsonValue !== null) return JSON.parse(jsonValue);

    if (typeof defaultValue === 'function') {
      return defaultValue();
    } else {
      return defaultValue;
    }
  });

  useEffect(() => {
    if (value === undefined) return storageObject.removeItem(key);
    storageObject.setItem(key, JSON.stringify(value));
  }, [key, value, storageObject]);

  const removeToken = useCallback(() => {
    setValue(undefined);
  }, []);

  return { value, setValue, removeToken };
}
